import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useData } from '../context/DataContext';
import SearchBar from '../components/SearchBar';
import CategoryFilter from '../components/CategoryFilter';
import ProductCard from '../components/ProductCard';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import { dataService } from '../services/dataService';
import SafeIcon from '../common/SafeIcon';
import QuickLinksDropdown from '../components/QuickLinksDropdown';
import HeroSection from '../components/HeroSection';
import Footer from '../components/Footer';
import ResultsPerPageSelector from '../components/ResultsPerPageSelector';
import PaginationControl from '../components/PaginationControl';
import * as FiIcons from 'react-icons/fi';

const { FiSearch, FiPackage, FiRefreshCw } = FiIcons;

const SearchPage = () => {
  const { products, loading, error, searchIndex, categories, refreshData } = useData();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All Categories');
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(50);
  const [correctedQuery, setCorrectedQuery] = useState(null);
  const [showOriginalResults, setShowOriginalResults] = useState(false);
  const navigate = useNavigate();
  
  // Apply category filter from navigation if present
  useEffect(() => {
    if (location.state?.selectedCategory) {
      setSelectedCategory(location.state.selectedCategory);
      // Clear the state to prevent persistent filter on reload
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);
  
  // First filter by search query
  const searchResults = useMemo(() => {
    if (!searchIndex) return [];
    
    if (!searchQuery.trim()) {
      return products;
    }
    
    // Check if we need to use the original query or the corrected one
    const queryToUse = showOriginalResults ? searchQuery : (correctedQuery || searchQuery);
    
    // Get search results
    return dataService.search(searchIndex, queryToUse, products);
  }, [searchIndex, searchQuery, products, correctedQuery, showOriginalResults]);

  // Then filter by category
  const filteredResults = useMemo(() => {
    return dataService.filterByCategory(searchResults, selectedCategory);
  }, [searchResults, selectedCategory]);

  const paginatedResults = useMemo(() => {
    const startIndex = (currentPage - 1) * resultsPerPage;
    return filteredResults.slice(startIndex, startIndex + resultsPerPage);
  }, [filteredResults, currentPage, resultsPerPage]);

  const totalPages = Math.ceil(filteredResults.length / resultsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, resultsPerPage]);

  useEffect(() => {
    // Check for misspelling when search query changes
    if (searchQuery && searchQuery.trim().length > 2) {
      const possibleCorrection = dataService.checkSpelling(searchQuery.trim(), products);
      if (possibleCorrection && possibleCorrection !== searchQuery.trim()) {
        setCorrectedQuery(possibleCorrection);
        setShowOriginalResults(false);
      } else {
        setCorrectedQuery(null);
      }
    } else {
      setCorrectedQuery(null);
    }
  }, [searchQuery, products]);

  const handleProductClick = (product) => {
    navigate(`/product/${product.id}`, { state: { product } });
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  };

  const handleShowOriginalResults = () => {
    setShowOriginalResults(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <ErrorMessage message={error} onRetry={refreshData} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-3 md:py-4">
            {/* Desktop Header */}
            <div className="hidden md:flex items-center justify-between">
              <div className="flex items-center">
                <Link to="/" className="text-2xl font-bold text-[#8d2424] hover:text-[#7a1e1e] transition-colors no-underline">
                  LiveWire Product Search Database
                </Link>
              </div>
              <div className="flex items-center space-x-4">
                <button
                  onClick={refreshData}
                  className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
                >
                  <SafeIcon icon={FiRefreshCw} className="text-lg" />
                  <span>Refresh Product Database</span>
                </button>
                <QuickLinksDropdown />
              </div>
            </div>
            
            {/* Mobile Header */}
            <div className="md:hidden">
              <div className="flex items-center justify-between mb-2">
                <Link to="/" className="text-xl font-bold text-[#8d2424] text-left hover:text-[#7a1e1e] transition-colors no-underline">
                  LiveWire Product Search Database
                </Link>
              </div>
              <div className="flex items-center justify-end space-x-2 mt-2">
                <button
                  onClick={refreshData}
                  className="flex items-center space-x-1 px-2 py-1.5 text-white bg-[#8d2424] rounded hover:bg-[#7a1e1e] transition-colors text-xs"
                >
                  <SafeIcon icon={FiRefreshCw} className="text-lg text-white" />
                  <span>Refresh Database</span>
                </button>
                <QuickLinksDropdown />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <HeroSection />

      {/* Main Content */}
      <div className="flex-grow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Search and Filter Section */}
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-sm mb-6">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search by Name, SKU, Brand..."
              products={products}
              searchIndex={searchIndex}
            />
            
            {/* Category Filter */}
            <div className="mt-4">
              <h3 className="text-lg font-medium text-gray-800 mb-2">Filter by Category</h3>
              <CategoryFilter
                categories={categories}
                selectedCategory={selectedCategory}
                onSelectCategory={setSelectedCategory}
              />
            </div>
          </div>

          {/* Results Summary */}
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
            <div className="flex flex-col mb-4 md:mb-0">
              <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
                <p className="text-gray-600 text-sm sm:text-base mb-2 sm:mb-0">
                  {searchQuery || selectedCategory !== 'All Categories' ? (
                    <>
                      <span className="font-medium">{filteredResults.length}</span> results
                      {searchQuery && (
                        <> for "<span className="font-medium">{searchQuery}</span>"</>
                      )}
                      {selectedCategory !== 'All Categories' && (
                        <> in <span className="font-medium">{selectedCategory}</span></>
                      )}
                    </>
                  ) : (
                    <>
                      Showing <span className="font-medium">{products.length}</span> products
                    </>
                  )}
                </p>
                {(searchQuery || selectedCategory !== 'All Categories') && (
                  <button
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCategory('All Categories');
                      setCorrectedQuery(null);
                      setShowOriginalResults(false);
                    }}
                    className="text-sm text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    Clear filters
                  </button>
                )}
              </div>
              
              {/* Spelling correction notice */}
              {correctedQuery && searchQuery && !showOriginalResults && (
                <div className="mt-2 text-sm">
                  <p className="text-[#8d2424] font-medium">
                    Showing results for "<strong>{correctedQuery}</strong>"
                  </p>
                  <button 
                    onClick={handleShowOriginalResults}
                    className="text-gray-800 hover:underline mt-1"
                  >
                    View search results for "<strong>{searchQuery}</strong>" instead
                  </button>
                </div>
              )}
            </div>
            
            {/* Results per page selector */}
            <div className="flex items-center">
              <ResultsPerPageSelector 
                value={resultsPerPage} 
                onChange={setResultsPerPage} 
              />
            </div>
          </div>

          {/* Results */}
          {filteredResults.length === 0 ? (
            <div className="text-center py-12">
              <SafeIcon icon={FiSearch} className="text-4xl text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {searchQuery || selectedCategory !== 'All Categories' ? 'No products found' : 'No products available'}
              </h3>
              <p className="text-gray-600">
                {searchQuery || selectedCategory !== 'All Categories' 
                  ? 'Try adjusting your search terms or category filter'
                  : 'Please check back later or contact your administrator'
                }
              </p>
            </div>
          ) : (
            <>
              <motion.div 
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                {paginatedResults.map((product, index) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: Math.min(index * 0.05, 0.5), duration: 0.3 }}
                  >
                    <ProductCard
                      product={product}
                      onClick={() => handleProductClick(product)}
                      onCategoryClick={handleCategoryClick}
                    />
                  </motion.div>
                ))}
              </motion.div>

              {/* Enhanced Pagination */}
              {totalPages > 1 && (
                <div className="mt-8">
                  <PaginationControl 
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default SearchPage;